import axios, { AxiosError, AxiosResponse, Method } from 'axios';

import { apiEndpoints } from 'constants/apiEndpoints';
import CONFIG from 'config';
import history from './history';
import { LOGIN_PATH } from 'pages/login/Login.constant';

export interface CHEndpoint {
  path: string;
  method: string;
}

function authHeader() {
  return localStorage.getItem('authorizationToken');
}

function isAuthenticationEndpoint(endpoint: string): boolean {
  return endpoint === apiEndpoints.MAGIC_LOGIN.path;
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem('authorizationToken', `Bearer ${String(token)}`);
  } catch (e) {
    console.log(e);
  }
}

export function deleteAuthToken(): boolean {
  try {
    localStorage.removeItem('authorizationToken');
    localStorage.removeItem('userInfo');
  } catch (e) {
    console.log(e);
    return false;
  }
  return true;
}

function checkTokenExpiry(response, endpoint) {
  if (response && response.errorCode && response.errorCode === 'UNAUTHORIZED') {
    if (!isAuthenticationEndpoint(endpoint)) {
      deleteAuthToken();
      window.location.replace(LOGIN_PATH);
    }
  }
}

function generateQueryParams(queryParams: { [key in string]: any }) {
  const esc = encodeURIComponent;
  return `?${String(
    Object.keys(queryParams)
      .map(k => `${esc(k)}=${esc(queryParams[k])}`)
      .join('&')
  )}`;
}

function formatEndpoint(
  endpoint: CHEndpoint,
  pathParams: { [key in string]: any } | undefined,
  queryParams: { [key in string]: any } | undefined
) {
  // generating fully qualified path for request
  let formattedEndpoint = endpoint.path;
  if (pathParams) {
    // Replacing url path params with actual provided values.
    Object.keys(pathParams).forEach(key => {
      formattedEndpoint = formattedEndpoint.replace(`{${String(key)}}`, pathParams[key]);
    });
  }

  if (formattedEndpoint.includes('{')) {
    // Some path params aren't replaced with actual values.
    throw new Error(
      'Path parameters are required and their keys must' +
        'match with the placeholder keys defined in endpoint declaration'
    );
  }
  if (queryParams) {
    // Appending Query params with url path
    formattedEndpoint += generateQueryParams(queryParams);
  }

  return formattedEndpoint;
}

export const signOff = (): void => {
  deleteAuthToken();
  history.push(LOGIN_PATH);
};

function methodType(method: string): Method {
  switch (method.toLowerCase()) {
    case 'get':
      return 'GET';
    case 'delete':
      return 'DELETE';
    case 'head':
      return 'HEAD';
    case 'options':
      return 'OPTIONS';
    case 'post':
      return 'POST';
    case 'put':
      return 'PUT';
    case 'patch':
      return 'PATCH';
    case 'purge':
      return 'PURGE';
    case 'link':
      return 'LINK';
    case 'unlink':
      return 'UNLINK';
    default:
      console.log('Invalid Type returning get');
      return 'GET';
  }
}

const servicePortMapping = {
  'report-service-node': 3000,
  profile: 8084,
  conversation: 8082,
  v2: 8090, // v2/scheduling
  scheduling: 8085,
  auth: 8083,
  billing: 8087,
  audit: 8081,
};

function getPortForService(service: string) {
  const serviceKey = Object.keys(servicePortMapping).find(key => service.includes(key));
  if (!serviceKey) return 9191;
  return servicePortMapping[serviceKey] ?? 9191;
}

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    return Promise.reject(error.response);
  }
);

export function baseRequest(
  endpoint: CHEndpoint,
  requestBody?: { [key in string]: any },
  pathParams?: { [key in string]: any },
  queryParams?: { [key in string]: any },
  requireAuth = true,
  isBase = true,
  isMultipart = false,
  formDataKey?: string
): Promise<AxiosResponse> {
  let endpointPath =
    pathParams || queryParams ? formatEndpoint(endpoint, pathParams, queryParams) : endpoint.path;

  let headers = {};

  headers = requireAuth
    ? { 'Content-Type': 'application/json', Authorization: authHeader() }
    : { 'Content-Type': 'application/json' };

  if (isMultipart) {
    headers = requireAuth
      ? { 'Content-Type': 'multipart/form-data', Authorization: authHeader() }
      : { 'Content-Type': 'multipart/form-data' };
  }

  let elkBaseUrl;
  let elkPrefix = '';
  if (!isBase) {
    elkBaseUrl = (CONFIG.api.elkUrl as string).split('/').slice(0, -1).join('/');
    elkPrefix = (CONFIG.api.elkUrl as string).split('/').pop() || '';
  }

  if (isMultipart && requestBody) {
    const formData = new FormData();
    if (formDataKey && formDataKey !== 'file') {
      formData.append(formDataKey, JSON.stringify(requestBody[formDataKey]));
      if (requestBody.file) {
        formData.append('file', requestBody.file);
      }
    } else {
      Object.keys(requestBody).forEach(key => {
        if (requestBody) {
          if (key === 'file') {
            formData.append(key, requestBody[key]);
          } else if (Array.isArray(requestBody[key])) {
            requestBody[key].forEach(item => formData.append(key, item));
          } else {
            formData.append(key, requestBody[key]);
          }
        }
      });
    }
    requestBody = formData;
  }

  let baseUrl = CONFIG.api.baseUrl as string;

  // for local environment
  if (baseUrl.includes('localhost')) {
    const serviceName = endpoint.path.split('/');
    const port = getPortForService(endpoint.path.charAt(0) === '/' ? serviceName[1] : serviceName[0]);
    baseUrl = baseUrl.replace('[port]', port);
    if (endpointPath.includes('report-service-node')) {
      if (endpointPath.charAt(0) === '/') {
        endpointPath = endpointPath.replace('/report-service-node/', '/');
      } else {
        endpointPath = endpointPath.replace('report-service-node/', '/');
      }
    }
  }

  const conf = {
    method: methodType(endpoint.method),
    baseURL: isBase ? baseUrl : elkBaseUrl,
    headers,
    url: `${elkPrefix}${endpointPath}`,
    data: requestBody,
  };

  return axios
    .request(conf)
    .then(response => {
      if (response && response.status === 200 && response.headers && response.data) {
        if (response.data.accessToken) {
          console.log('authorization found');
          setAuthToken(response.data.accessToken);
        }
      }
      return response;
    })
    .catch(error => {
      if (error?.status === 401) {
        if (!isAuthenticationEndpoint(endpoint.path)) {
          deleteAuthToken();
          window.location.replace(LOGIN_PATH);
        } else {
          return Promise.reject(error.data?.errors?.[0]?.endUserMessage || 'Unauthorized');
        }
      }

      if (error?.response) {
        // The client was given an error response (5xx, 4xx)
        if (error.response.status && error.response.status === 404) {
          throw new Error(
            `404 error occured. Reason:  ${String(error.response.data.error)} ENDPOINT : ${String(
              error.response.config.url
            )}`
          );
        } else if (error.response.status && error.response.status === 401) {
          if (!isAuthenticationEndpoint(endpoint.path)) {
            console.log('Routing to Login');
            window.location.replace(LOGIN_PATH);
          }
        } else if (error.response.status && error.response.status !== 200) {
          throw new Error(`An unexpected error occurred. Reason:  ${String(error.response.data.message)}`);
        }
        checkTokenExpiry(error.response, endpoint);
      }
      return Promise.reject(error);
    });
}
